/**
 * Chord Quality Demo
 *
 * Demonstrates the emotional difference between major and minor chords
 * with recognizable song references to help users remember the sound.
 */

import React, { useState } from "react";
import { useSoundLibrary } from "../../soundLibrary";
import { Note } from "../../types/sound";
import styles from "../../styles/ChordQualityDemo.module.css";

// Chord definitions with song references
const CHORDS = {
    major: {
        name: "Major Triad",
        quality: "bright",
        emoji: "☀️",
        songReference: "\"Happy Birthday\" - the first two notes \"Hap-py\"",
        description: "Happy, triumphant, resolved",
        notes: ["C4", "E4", "G4"] as Note[],
    },
    minor: {
        name: "Minor Triad",
        quality: "dark",
        emoji: "🌙",
        songReference: "\"Greensleeves\" / \"What Child is This?\" - the opening",
        description: "Sad, serious, melancholic",
        notes: ["A3", "C4", "E4"] as Note[],
    },
};

const INSTRUMENT = "piano";
const CHORD_DURATION = 2.0; // seconds

export default function ChordQualityDemo() {
    const { loadInstrument, playChord } = useSoundLibrary();
    const [activeChord, setActiveChord] = useState<"major" | "minor" | null>(null);

    const handlePlay = async (chordType: "major" | "minor") => {
        setActiveChord(chordType);
        await loadInstrument(INSTRUMENT);
        await playChord(INSTRUMENT, CHORDS[chordType].notes, {
            duration: CHORD_DURATION,
        });
        // Wait for chord to finish playing before clearing active state
        setTimeout(() => setActiveChord(null), CHORD_DURATION * 1000);
    };

    return (
        <div className={styles.demoContainer}>
            <div className={styles.header}>
                <h4 className={styles.title}>Chord Quality Compare</h4>
                <p className={styles.subtitle}>
                    Hear the emotional difference between major and minor chords
                </p>
            </div>

            <div className={styles.chordsGrid}>
                {(["major", "minor"] as const).map((type) => {
                    const chord = CHORDS[type];
                    const isActive = activeChord === type;

                    return (
                        <div
                            key={type}
                            className={`${styles.chordCard} ${isActive ? styles.active : ""}`}
                        >
                            <div className={styles.chordHeader}>
                                <span className={styles.emoji}>{chord.emoji}</span>
                                <h5 className={styles.chordName}>{chord.name}</h5>
                            </div>

                            <button
                                className={`${styles.playButton} ${isActive ? styles.playing : ""}`}
                                onClick={() => handlePlay(type)}
                                disabled={isActive}
                            >
                                {isActive ? "♪ Playing..." : "▶ Play Chord"}
                            </button>

                            <div className={styles.chordInfo}>
                                <p className={styles.description}>
                                    <strong>{chord.quality}</strong> – {chord.description}
                                </p>
                                <div className={styles.songReference}>
                                    <span className={styles.musicNote}>🎵</span>
                                    <p className={styles.songText}>{chord.songReference}</p>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className={styles.tip}>
                <p className={styles.tipText}>
                    <strong>💡 Tip:</strong> Major chords feel "home" and happy. Minor chords feel
                    serious or sad. This emotional quality is why composers choose them to match
                    the mood they want to create.
                </p>
            </div>
        </div>
    );
}
